import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { alarmDetailsSelector } from 'features/details/exports';
import { loadFirstMomentRecordings, FirstMomentRecordingFile } from './loadRecordings';
import { RecordingBox } from '../recordingPlayer';

const pollIntervalMs = 5000;
const maxFailures = 5;

type PlayableRecording = Pick<FirstMomentRecordingFile, 'recordingId' | 'version' | 'chunkDurations' | 'lastUploadAt' | 'final'> & {
  url: string;
  /** The same audio, for reading it without fetching the url. */
  blob: Blob;
  /** The url before this one: still playing until the player has taken over the new one. */
  previousUrl?: string;
};

/**
 * The audio the app recorded from the alarm button press until somebody joined the call. It is shown where
 * the legacy recording is shown, and polls while the call is open so new chunks are added.
 */
const FirstMomentRecording: FC = () => {

  const alarm = useSelector(alarmDetailsSelector);
  const alarmId = alarm?._id;
  const alarmCreatedAt = alarm?.createdAt;
  const [recordings, setRecordings] = useState<PlayableRecording[]>([]);

  useEffect(() => {

    if (!alarmId || !alarmCreatedAt) return undefined;

    const controller = new AbortController();
    const cache = new Map<string, ArrayBuffer>();
    const urls = new Map<string, PlayableRecording>();
    let timer: number | undefined;
    let failures = 0;

    const toPlayable = (recording: FirstMomentRecordingFile): PlayableRecording => {

      const known = urls.get(recording.recordingId);

      if (known?.version === recording.version) return known;

      // The player may still be playing the url before this one; only the one before that can go.
      if (known?.previousUrl) URL.revokeObjectURL(known.previousUrl);

      const { blob, ...rest } = recording;
      const playable = {
        ...rest, blob, url: URL.createObjectURL(blob), previousUrl: known?.url,
      };

      urls.set(recording.recordingId, playable);

      return playable;

    };

    const load = async () => {

      let keepPolling = true;

      try {

        const result = await loadFirstMomentRecordings(alarmId, alarmCreatedAt, cache, controller.signal);

        if (controller.signal.aborted) return;

        keepPolling = result.keepPolling;
        failures = 0;
        setRecordings(result.recordings.map(toPlayable));

      } catch (error) {

        if (controller.signal.aborted) return;

        // Try again on the next poll, but not forever (e.g. a download that is blocked or forbidden).
        failures += 1;

      }

      if (keepPolling && failures < maxFailures) timer = window.setTimeout(load, pollIntervalMs);

    };

    load();

    return () => {

      controller.abort();
      window.clearTimeout(timer);
      urls.forEach(({ url, previousUrl }) => {

        URL.revokeObjectURL(url);

        if (previousUrl) URL.revokeObjectURL(previousUrl);

      });

    };

  }, [alarmId, alarmCreatedAt]);

  if (recordings.length === 0) return null;

  return (
    <>
      {recordings.map(({
        recordingId, url, blob, chunkDurations, lastUploadAt, final,
      }) => (
        <RecordingBox
          key={recordingId}
          url={url}
          blob={blob}
          recordingKey={recordingId}
          chunkDurations={chunkDurations}
          lastUploadAt={lastUploadAt}
          final={final}
        />
      ))}
    </>
  );

};

export default FirstMomentRecording;
